/**
 * Detect Phase-1 programmatic SEO landing pathnames.
 * Full validation stays on the Laravel SeoLandingUrlResolver via /seo/resolve.
 */

const SEO_RESERVED_EXACT = new Set([
  '',
  'properties',
  'property',
  'propertylist',
  'propertylisting',
  'blog',
  'blogs',
  'magazine',
  'media',
  'ourproducts',
  'api',
  'admin',
  'crm',
  'images',
  'sitemap',
  'assets',
])

export function normalizeSeoPathname(pathname: string): string {
  const raw = (pathname || '/').split('?')[0].split('#')[0]
  let p = raw.toLowerCase().replace(/\/+/g, '/')
  if (p.length > 1) p = p.replace(/\/+$/, '')
  return p.startsWith('/') ? p : `/${p}`
}

export function isSeoLandingPathname(pathname: string): boolean {
  const path = normalizeSeoPathname(pathname)
  const p = path.replace(/^\//, '')
  if (!p || SEO_RESERVED_EXACT.has(p)) return false
  if (/^(property|propertylist|propertylisting|blog|blogs|magazine|media|ourproducts|api|admin|crm|images|sitemap|assets)\//.test(p)) {
    return false
  }
  // properties-in-{city}, property-for-sale-in-{city}, {cat}-for-rent-in-{area}-{city},
  // {cat}-in-{city}, and any of those with a "{n}-bhk-" prefix.
  return /^[a-z0-9-]+-in-[a-z0-9-]+$/.test(p)
}

/** "2 BHK" → 2 when it can be a path prefix (1–10 BHK; not "1 RK" / "5+ BHK"). */
export function bhkPathNumber(value: string | undefined): number | null {
  const m = /^\s*(\d{1,2})\s*bhk\s*$/i.exec(value ?? '')
  if (!m) return null
  const n = Number(m[1])
  return n >= 1 && n <= 10 ? n : null
}

/** Query params that conflict with SEO path authority. */
export const SEO_LOCKED_FILTER_KEYS = ['city', 'intent', 'category', 'area', 'bhk'] as const

export type SeoLockedFilterKey = (typeof SEO_LOCKED_FILTER_KEYS)[number]

/** True when the SEO landing URL has extra listing/query filters (→ noindex). */
export function seoPathHasExtraFilters(search: string): boolean {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  for (const value of params.values()) {
    if (!value) continue
    return true
  }
  return false
}
